<template>
  <div
    id="product-form"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form
      class="product-form__form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        @update-variant="handleUpdateVariant"
      />

      <quantity-selector
        v-if="section.displayQuantitySelector"
        class="product-form__quantity-selector"
        large
        :min="1"
        :quantity="quantity"
        :state="loading ? 'loading' : 'ready'"
        @update-quantity="handleUpdateQuantity"
      />

      <input
        v-else
        name="quantity"
        readonly
        type="hidden"
        :value="quantity"
      >

      <recipient-form
        v-if="product.giftCard"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />

      <btn
        class="product-form__add-to-cart"
        :label="addToCartText"
        :show-state-icon="state === 'adding'"
        type="submit"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import ProductOptions from '~async/product-form/product-options'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import RecipientForm from '~async/product-form/recipient-form'

import IconLoading from '~icons/general/loading.svg'

import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    IconLoading,
    NotificationPanel,
    ProductOptions,
    QuantitySelector,
    RecipientForm,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        displayRecipientForm: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: true,
        id: '',
        initialSelect: true,
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
      }),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      quantity: 1,
      state: 'ready',
      timeout: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant) {
        return this.$string('product.cta.select_variant')
      }

      if (!this.variant.available) {
        return this.$string('product.cta.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.product.giftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null
      }

      const filteredFields = {}

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return
        }

        filteredFields[key] = value
      })

      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      }
    },

    /**
     * Compute show product form element.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.loading && !this.product) {
        return true
      }

      const multipleOptions = this.product?.options?.some((option) => {
        return option.values.length > 1
      })

      /**
       * If multiple variants then ensure they have more than one option.
       */
      const showOptionsMultiVariant =
        !this.product.hasOnlyDefaultVariant &&
        multipleOptions

      /**
       * If single variant then check if section settings enable display.
       */
      const showOptionsSingleVariant =
        this.section.displaySingleValueOptions &&
        this.section.displaySingleVariantOption

      return showOptionsMultiVariant || showOptionsSingleVariant
    },
  },

  watch: {

    /**
     * Watch product to reset quantity and error.
     */
    product() {
      this.quantity = 1
      this.error = ''
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (!this.variant) {
        this.error = this.$string('product.form.select_variant')
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        return
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
              ...this.giftCardLineItemProperties,
              ...this.lineItemProperties,
            }
            : this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        if (
          this.section.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await cnvs.Sleep(this.$timing('message'))
      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard && this.section.resetRecipientFormOnAdd) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        }
      }

      /**
       * Reset variant selection based on setting.
       */
      if (this.section.resetFormBehaviour !== 'clear') {
        return
      }

      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update quantity event.
     * @param {String} quantity - Emitted quantity.
     */
    handleUpdateQuantity(quantity) {
      this.quantity = quantity
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },
  },
}
</script>

<style lang="scss">
@import './product-form';
</style>
